import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, UserPlus, CheckCircle2, ArrowLeft } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from '@nimbitlabs/ui';
import { toast } from 'sonner';

interface SignupProps {
  apiBaseUrl?: string;
  loginLink?: string;
  homeLink?: string;
  logo?: React.ReactNode;
  appName?: string;
  onSuccess?: () => void;
  onValidateReferral?: (code: string) => Promise<{ isValid: boolean; companyName?: string; message?: string }>;
}

export function Signup({
  apiBaseUrl,
  loginLink = '/login',
  homeLink = '/',
  logo,
  appName = 'App',
  onSuccess,
  onValidateReferral,
}: SignupProps) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    companyName: ''
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [referralCode, setReferralCode] = useState<string>('');
  const [referralInfo, setReferralInfo] = useState<{ isValid: boolean; companyName?: string; message?: string } | null>(null);
  const [isValidatingReferral, setIsValidatingReferral] = useState(false);

  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) {
      setReferralCode(ref);
      validateReferralCode(ref);
    }
  }, [searchParams]);

  const validateReferralCode = async (code: string) => {
    if (!code || !onValidateReferral) {
      setReferralInfo(null);
      return;
    }

    setIsValidatingReferral(true);
    try {
      const result = await onValidateReferral(code);
      setReferralInfo(result);
    } catch (err) {
      console.error('Error validating referral code:', err);
      setReferralInfo({ isValid: false, message: 'Unable to validate referral code' });
    } finally {
      setIsValidatingReferral(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }

    if (!formData.email.trim()) {
      setError('Email is required');
      return;
    }

    if (!formData.companyName.trim()) {
      setError('Company name is required');
      return;
    }

    setIsLoading(true);

    try {
      const url = apiBaseUrl ? `${apiBaseUrl}/accounts/signup` : '/accounts/signup';
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          name: formData.name,
          companyName: formData.companyName,
          phone: formData.phone || null,
          referralCode: referralCode || null
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create account');
      }

      toast.success('Account created!', {
        description: 'Please log in to access your account.',
      });

      navigate(`${loginLink}?email=${encodeURIComponent(formData.email)}`);
      onSuccess?.();
    } catch (err: any) {
      console.error('Signup error:', err);
      if (err.message?.includes('UsernameExistsException') || err.message?.includes('already exists')) {
        setError('An account with this email already exists. Please sign in instead.');
        setTimeout(() => {
          navigate(`${loginLink}?email=${encodeURIComponent(formData.email)}`);
        }, 2000);
      } else {
        setError(err.message || 'Signup failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-muted/20 p-4">
      <Card className="w-full max-w-md border-border/50 shadow-xl">
        <CardHeader className="space-y-1 pb-6">
          {/* Back to Home Button */}
          <Button
            variant="ghost"
            size="sm"
            asChild
            className="w-fit mb-4"
          >
            <Link to={homeLink}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Link>
          </Button>
          
          <div className="flex items-center justify-center mb-4">
            {logo || (
              <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
                <UserPlus className="h-6 w-6 text-primary-foreground" />
              </div>
            )}
          </div>
          <CardTitle className="text-2xl text-center text-foreground">
            Welcome to {appName}
          </CardTitle>
          <CardDescription className="text-center text-muted-foreground">
            Let's create your account and get you started in minutes
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-foreground">What's your name?</Label>
              <Input
                id="name"
                name="name"
                type="text"
                placeholder="e.g., John Doe"
                value={formData.name}
                onChange={handleInputChange}
                required
                className="bg-input border-border/50"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-foreground">Work Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="e.g., john@company.com"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="bg-input border-border/50"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="companyName" className="text-foreground">Company Name</Label>
              <Input
                id="companyName"
                name="companyName"
                type="text"
                placeholder="e.g., Acme Inc."
                value={formData.companyName}
                onChange={handleInputChange}
                required
                className="bg-input border-border/50"
              />
            </div>
            
            {referralCode && (
              <div className="space-y-2">
                <Label className="text-foreground">Referral Code</Label>
                {isValidatingReferral ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Validating referral code...
                  </div>
                ) : referralInfo?.isValid ? (
                  <div className="flex items-center gap-2 p-3 bg-accent/10 border border-accent/30 rounded-md">
                    <CheckCircle2 className="h-4 w-4 text-accent" />
                    <div className="text-sm">
                      <p className="font-medium text-foreground">
                        Referred by {referralInfo.companyName}
                      </p>
                    </div>
                  </div>
                ) : (
                  <Alert variant="destructive">
                    <AlertDescription>
                      {referralInfo?.message || 'Invalid referral code'}
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="phone" className="text-foreground">Phone Number (optional)</Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                placeholder="e.g., +1 (555) 123-4567"
                value={formData.phone}
                onChange={handleInputChange}
                className="bg-input border-border/50"
              />
              <p className="text-xs text-muted-foreground">
                For important account updates and support
              </p>
            </div>
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isLoading ? 'Creating your account...' : 'Get Started Free'}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-muted-foreground">
            Already have an account?{' '}
            <Link to={loginLink} className="text-primary hover:text-primary/80">
              Sign in
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default Signup;
